import * as React from "react";
import { Item } from "@/types/fse";
import { Button } from "@/components/ui/button";
import { FormProvider, useForm } from "react-hook-form";
import { FormInput } from "@/components/forms/input";
import { AddGroupModalContentStep2Dnd } from "./step2Dnd";
import { useCreateDocumentGroupMutation } from "@/modules/documentGroup/documentGroup.api";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { FILES_TAGS, filesApi } from "@/api/files";
import { useAppDispatch } from "@/store/hooks";
import TopBarProgress from "react-topbar-progress-indicator";

interface Props {
  selectedFiles: Item[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<Item[]>>;
  handleClose: () => void;
  setStep: React.Dispatch<React.SetStateAction<1 | 2>>;
  parentFolderId?: string;
}

interface FormInputs {
  name: string;
}

export const AddGroupModalContentStep2: React.FC<Props> = ({
  setStep,
  parentFolderId,
  handleClose,
  setSelectedFiles,
  selectedFiles,
}) => {
  const methods = useForm<FormInputs>();
  const dispatch = useAppDispatch();

  const [handleCreate, { isLoading }] = useCreateDocumentGroupMutation();

  const onSubmit = async (payload: FormInputs) => {
    try {
      await handleCreate({
        name: payload.name,
        parent_fse_id: parentFolderId,
        items: selectedFiles.map(file => file.id),
      }).unwrap();
      toast.success("Group Added");
      dispatch(filesApi.util.invalidateTags([FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_TAG]));
      handleClose();
    } catch (error) {
      toast.error("Error while saving");
      Sentry.captureException(error);
      return;
    }
  };

  const handleDelete = (item: Item) => {
    setSelectedFiles(prev => prev.filter(row => row.id !== item.id));
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <FormProvider {...methods}>
      {isLoading && <TopBarProgress />}
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex flex-grow flex-col space-y-4 overflow-hidden">
          <FormInput name="name" required={true} placeholder="Enter group name" className="my-3" />

          <AddGroupModalContentStep2Dnd
            handleDelete={handleDelete}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
          />
        </div>
        <div className="mt-4 flex justify-between">
          <Button type="button" variant="outline" onClick={handleBack}>
            Back
          </Button>

          <Button variant="default" type="submit" disabled={!selectedFiles.length || isLoading}>
            {isLoading ? "Creating..." : "Create Group"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
